import { Group, Plane, Ray, Raycaster, Vector3, Vector2 } from "three";
import { InputHandler } from "../utils/input-handler/input-handler";
import { Viewer } from "../viewer";
import { Polygon } from "./polygon-drawing";
import * as Utils from "../utils/utils";
import { SCENE_TYPE } from "../config/ViewerConfig";

export interface ISelectionData {
  selectionType: string;
  cartesianPoints: number[][];
  nextPoint: number[][];
  normal: number[][];
  clippingPlanesArray: number[][];
  clippingPlanesNormals: number[][];
  centerPosition: number[];
}

export class CanvasPolygonSelection {
  private _drawing: Polygon;

  private _viewer: Viewer;
  private _inputHandler: InputHandler;
  private _canvas: HTMLCanvasElement;

  // private _selectionResolve: any;
  // private _selectionReject: any;

  private _debugGroup: Group;

  public get isDrawingDone() {
    return !!this._drawing;
  }

  constructor(viewer: Viewer) {
    this._viewer = viewer;
    this._debugGroup = new Group();
    this._viewer.getScene(SCENE_TYPE.POINTPICK).add(this._debugGroup);
  }

  public start(
    resolve: any,
    reject: any,
    onPointClick?: (points: number[][], count: number, total?: number) => void
  ): void {
    this._canvas = this.createCanvas();
    this._inputHandler = new InputHandler(this._viewer, this._canvas);
    const mainCanvas = this._viewer.getRenderer().domElement;
    const container = mainCanvas.parentElement;
    this._canvas.style.zIndex = mainCanvas.style.zIndex;
    this._canvas.height = mainCanvas.clientHeight;
    this._canvas.width = mainCanvas.clientWidth;
    this._canvas.style.height = mainCanvas.style.height;
    this._canvas.style.width = mainCanvas.style.width;
    container.insertBefore(this._canvas, mainCanvas);
    this._drawing = new Polygon(this._canvas, this._viewer);
    this._inputHandler.registerHandler("polygon_drawing", this._drawing);

    const onDrawingOnResolve = () => {
      resolve(this.getTriangulatedSelectionData(this._viewer));
    };

    const onDrawingOnReject = () => {
      reject("Drawing could not be completed");
    };

    const onDrawingOnProgress = (pointvec: Vector2[], count: number) => {
      if (
        this._viewer.poligonIndicesCount +
          this._drawing.getPickPointsLength() <=
        this._viewer.poligonMaxCount
      ) {
        let total =
          this._viewer.poligonIndicesCount +
          this._drawing.getPickPointsLength();

        onPointClick &&
          onPointClick(
            pointvec.map((p) => [p.x, p.y]),
            count,
            total
          );
      }
    };

    this._drawing.start(
      onDrawingOnResolve,
      onDrawingOnReject,
      onDrawingOnProgress
    );

    // this._selectionResolve = resolve;
    // this._selectionReject = reject;
  }

  public dispose() {
    this._drawing.dispose();
    this._canvas.remove();
    // this._selectionReject &&
    //   this._selectionReject("Selection has been cancelled.");
  }

  public finish() {
    this._inputHandler.unregisterHandler("polygon_drawing");
    this._drawing.finish();
  }

  public undoDrawing() {
    this._drawing.undo();
  }

  public resetDrawing() {
    this._drawing.restart();
  }

  public setVisibility(visible: boolean): void {
    this._drawing.setVisibility(visible);
  }

  public getPoints(viewer: Viewer): Vector3[] {
    const points: Vector3[] = [];
    const { points: points2d } = this._drawing.drawingData;
    if (viewer) {
      const camera = viewer.getCamera();
      const worldDirection = camera.getWorldDirection(new Vector3());
      const planeInfrontOfCamera = new Plane(worldDirection, 0);
      const rayCaster = new Raycaster();
      for (const point of points2d) {
        const coords = Utils.CalculateMousePosition(
          { clientX: point.x, clientY: point.y },
          this._canvas
        );
        rayCaster.setFromCamera(coords, camera);

        const pointOnPlane = rayCaster.ray.intersectPlane(
          planeInfrontOfCamera,
          new Vector3()
        );
        points.push(pointOnPlane);
      }
    }
    return points;
  }

  public getTriangles(viewer: Viewer): Vector3[][] {
    const points: Vector3[] = [];
    const { points: points2d } = this._drawing.drawingData;
    if (viewer) {
      const camera = viewer.getCamera();
      const worldDirection = camera.getWorldDirection(new Vector3());
      const planeInfrontOfCamera = new Plane(worldDirection, 0);
      const rayCaster = new Raycaster();
      for (const point of points2d) {
        const coords = Utils.CalculateMousePosition(
          { clientX: point.x, clientY: point.y },
          this._canvas
        );
        rayCaster.setFromCamera(coords, camera);

        const pointOnPlane = rayCaster.ray.intersectPlane(
          planeInfrontOfCamera,
          new Vector3()
        );
        points.push(pointOnPlane);
      }
    }
    const triangles = [];
    const indices = Utils.triangulateXY(points);
    for (let i = 0; i < indices.length; i += 3) {
      const triangle = [];
      triangle.push(points[indices[i]]);
      triangle.push(points[indices[i + 1]]);
      triangle.push(points[indices[i + 2]]);
      triangles.push(triangle);
    }
    return triangles;
  }

  private getNormalsUsingPerspectiveCamera(
    viewer: Viewer,
    midPoints: Vector3[],
    cornerPoints: Vector3[]
  ): Vector3[] {
    const cameraPosition = viewer.getCamera().position;
    const normals = [];

    for (let index = 0; index < midPoints.length; ++index) {
      const direction = new Vector3().subVectors(
        midPoints[index],
        cornerPoints[index]
      );
      direction.normalize();

      const forward = new Vector3().subVectors(
        midPoints[index],
        cameraPosition
      );
      forward.normalize();

      const normal = new Vector3().crossVectors(forward, direction);
      normal.normalize();
      normals.push(normal);
    }
    return normals;
  }

  private getNormalsUsingOrthographicCamera(
    viewer: Viewer,
    midPoints: Vector3[],
    cornerPoints: Vector3[]
  ): Vector3[] {
    const forward = viewer.getCamera().getWorldDirection(new Vector3());
    const normals = [];

    for (let index = 0; index < midPoints.length; ++index) {
      const direction = new Vector3().subVectors(
        midPoints[index],
        cornerPoints[index]
      );
      direction.normalize();

      const normal = new Vector3().crossVectors(forward, direction);
      normal.normalize();
      normals.push(normal);
    }
    return normals;
  }

  //normal points always inside
  public getTriangulatedSelectionData(viewer: Viewer): ISelectionData[] {
    const trianglePoints = this.getTriangles(viewer);
    const data: ISelectionData[] = [];

    for (let i = 0; i < trianglePoints.length; ++i) {
      const triangleCornerPoints: Vector3[] = [];
      const triangleCartesianPoints = [];
      const triangleCenterPosition = new Vector3();
      for (let j = 0; j < trianglePoints[i].length; ++j) {
        const nextIndex = (j + 1) % trianglePoints[i].length;
        const midPoint = new Vector3()
          .addVectors(trianglePoints[i][j], trianglePoints[i][nextIndex])
          .multiplyScalar(0.5);
        triangleCornerPoints.push(trianglePoints[i][j]);
        triangleCartesianPoints.push(midPoint);
        triangleCenterPosition.add(midPoint);
      }

      let triangleNormals: Vector3[];
      if (viewer.getCamera().type === "OrthographicCamera") {
        triangleNormals = this.getNormalsUsingOrthographicCamera(
          viewer,
          triangleCartesianPoints,
          triangleCornerPoints
        );
      } else {
        triangleNormals = this.getNormalsUsingPerspectiveCamera(
          viewer,
          triangleCartesianPoints,
          triangleCornerPoints
        );
      }

      //check if normals are pointing inside
      const testRay = new Ray(triangleCartesianPoints[0], triangleNormals[0]);
      let testPlane = new Plane();
      let isFacingInside = false;
      for (let j = 1; j < triangleCartesianPoints.length; ++j) {
        testPlane.setFromNormalAndCoplanarPoint(
          triangleNormals[j],
          triangleCartesianPoints[j]
        );
        if (testRay.intersectsPlane(testPlane)) {
          if (testRay.direction.dot(testPlane.normal) < 0) {
            isFacingInside = true;
            break;
          }
        }
      }

      if (!isFacingInside) {
        for (const normal of triangleNormals) {
          normal.negate();
        }
      }

      for (let i = 0; i < trianglePoints.length; ++i) {
        triangleCenterPosition.multiplyScalar(1 / trianglePoints[i].length);
      }

      data.push({
        cartesianPoints: triangleCartesianPoints.map((v) => v.toArray()), //3 mid points
        centerPosition: this._viewer.getOffset().toArray(), //1 number
        clippingPlanesArray: [],
        clippingPlanesNormals: [],
        nextPoint: [],
        normal: triangleNormals.map((n) => n.toArray()), //3 direction
        selectionType: "polygon",
      });
    }

    return data;
  }
  // ToDo
  // public getTriangulatedSelectionData;

  //normal points always inside
  // public getSelectionData(viewer: Viewer): ISelectionData{

  //   const cornerPoints = this.getPoints(viewer);
  //   const cartesianPoints = [];
  //   const centerPosition = new Vector3();

  //   for (let index = 0; index < cornerPoints.length; ++index) {
  //     const nextIndex = (index + 1) % cornerPoints.length;
  //     const midPoint = new Vector3().addVectors(cornerPoints[index], cornerPoints[nextIndex]).multiplyScalar(0.5);
  //     cartesianPoints.push(midPoint);
  //     centerPosition.add(midPoint);
  //   }

  //   let normals: Vector3[];
  //   if(viewer.getCamera().type === "OrthographicCamera"){
  //     normals = this.getNormalsUsingOrthographicCamera(viewer, cartesianPoints, cornerPoints);
  //   }
  //   else{
  //     normals = this.getNormalsUsingPerspectiveCamera(viewer, cartesianPoints, cornerPoints);
  //   }

  //   //check if normals are pointing inside
  //   const testRay = new Ray(cartesianPoints[0],normals[0]);
  //   let testPlane = new Plane();
  //   let isFacingInside = false;
  //   for(let i = 1; i< cartesianPoints.length;++i){
  //     testPlane.setFromNormalAndCoplanarPoint(normals[i], cartesianPoints[i]);
  //     if(testRay.intersectsPlane(testPlane)){
  //       if(testRay.direction.dot(testPlane.normal) < 0){
  //         isFacingInside = true;
  //         break;
  //       }
  //     }
  //   }

  //   if(!isFacingInside){
  //     for(const normal of normals){
  //       normal.negate();
  //     }
  //   }

  //   centerPosition.multiplyScalar(1/cornerPoints.length);

  //   const data: ISelectionData = {
  //     cartesianPoints: cartesianPoints.map(v=>v.toArray()),
  //     centerPosition: centerPosition.toArray(),
  //     clippingPlanesArray: [],
  //     clippingPlanesNormals: [],
  //     nextPoint: [],
  //     normal: normals.map(n=>n.toArray()),
  //     selectionType: "polygon",
  //   };

  //   return data;
  // }

  private createCanvas() {
    const canvas = document.createElement("canvas");
    // canvas.setAttribute("touch-action", "none");
    const canvasStyle = canvas.style;
    canvasStyle.position = "absolute";
    canvasStyle.left = "0";
    canvasStyle.top = "0";
    // canvasStyle.width = "100%";
    // canvasStyle.height = "100%";
    // canvasStyle.touchAction = "none";
    return canvas;
  }

  public copyMainCanvasSize(canvas: HTMLCanvasElement) {
    if (this._canvas) {
      this._canvas.height = canvas.clientHeight;
      this._canvas.width = canvas.clientWidth;
      this._canvas.style.height = canvas.style.height;
      this._canvas.style.width = canvas.style.width;
      this._drawing.redraw();
    }
  }
}
